import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { calculateTotal } from '@/lib/invoiceCalculations';
import { UpdateEstimateDto, ContactDTO, ContactCreateDTO, ContactUpdateDTO, EstimateStatus, ConvertEstimateToJobDto, Technician, JobItemOperationDTO, CompanyInvoiceDTO } from '@/types';
import { formatInstantInCompanyTimeZone, formatDateOnlyInCompanyTimeZone, toDateTimeInputValueInTz } from '@/lib/dateUtils';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { X, FileText, Plus, User, ArrowRightCircle, Download, MapPin, Share2, AlertCircle, Edit3, Pencil, Briefcase, DollarSign, ArrowUpRight, Search, Star, Paperclip, Send } from 'lucide-react';
import { JobDetailsModal } from './JobDetailsModal';
import { EstimateAttachmentsModal } from './EstimateAttachmentsModal';
import { JobItemsSection } from './ui/JobItemsSection';
import { EstimatePaymentsSection, EstimateTakePaymentButton } from './EstimatePaymentsSection';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { ContactFormModal } from './contacts/ContactFormModal';
import { ContactSearchModal } from './contacts/ContactSearchModal';
import { SendSmsModal } from '@/components/SendSmsModal';
import { SmsMethodSelector } from '@/components/SmsMethodSelector';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useVisualKeyboardOpen } from '@/hooks/useVisualKeyboardOpen';
import { ShareModal } from '@/components/ShareModal';
import { getAppOrigin, openDownloadUrl } from '@/lib/capacitor';
import { SignaturePad } from './public/SignaturePad';
import { EditableField } from './ui/EditableField';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { EditableAddress } from './ui/EditableAddress';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { buildTechnicianSelectOptions, formatTechnicianOptionLabel, hasInactiveTechnicians } from '@/lib/technicianSelectors';

interface EstimateDetailsModalProps {
    isOpen: boolean;
    estimateId: string | null;
    onClose: () => void;
    onEstimateUpdated?: () => void;
    /** 'convert': open straight into the Convert to Job dialog (the list's convert icon). */
    initialAction?: 'convert';
}

/**
 * A dialog on top of the estimate: its own layer (depth + 1 through the parent's LayerDepthContext),
 * portaled to body like the estimate itself so the z-index compares in one stacking context.
 * children gets the close handler that also pops the Back-navigation entry.
 */
function LayeredDialog({ onClose, historyState, closeOnBackdrop = false, children }: {
    onClose: () => void;
    historyState: Record<string, unknown>;
    closeOnBackdrop?: boolean;
    children: (close: () => void) => ReactNode;
}) {
    const layer = useModalLayer({ enabled: true, onClose, historyState });
    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
            <div className="fixed inset-0 flex items-center justify-center p-4" style={layer.style} role="dialog" aria-modal="true">
                <div className="fixed inset-0 bg-black/50" onClick={closeOnBackdrop ? layer.onClose : undefined} />
                {children(layer.onClose)}
            </div>
        </LayerDepthContext.Provider>,
        document.body,
    );
}



interface PendingContact {
    id: string;
    contact: ContactDTO;
    createPayload?: ContactCreateDTO;
    notes?: string;
    isPrimary: boolean;
    isNew?: boolean;
    isDeleted?: boolean;
}

const statusColors: Record<EstimateStatus, { bg: string; text: string }> = {
    Draft: { bg: 'bg-gray-100', text: 'text-gray-600' },
    Sent: { bg: 'bg-blue-100', text: 'text-blue-700' },
    Converted: { bg: 'bg-green-100', text: 'text-green-700' },
    Expired: { bg: 'bg-red-100', text: 'text-red-700' },
};

export function EstimateDetailsModal({ isOpen, estimateId, onClose, onEstimateUpdated, initialAction }: EstimateDetailsModalProps) {
    const isMobile = useIsMobile();
    const isKeyboardOpen = useVisualKeyboardOpen(isOpen && isMobile);
    const { selectedCompanyId } = useGlobalCompany();
    const companyTz = useCompanyTimezone();
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { openJob } = useVoiceCall();

    // Form state
    const [date, setDate] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [notes, setNotes] = useState('');
    const [address, setAddress] = useState('');
    const [unitName, setUnitName] = useState('');
    const [jobTypeId, setJobTypeId] = useState('');
    const [assignedTechnicianId, setAssignedTechnicianId] = useState('');
    const [showInactiveTechnicians, setShowInactiveTechnicians] = useState(false);
    const [scheduleDate, setScheduleDate] = useState(() => toDateTimeInputValueInTz(new Date(), companyTz));
    const [status, setStatus] = useState<EstimateStatus>('Draft');
    const [contacts, setContacts] = useState<PendingContact[]>([]);

    // Modal states
    const [isContactFormOpen, setContactFormOpen] = useState(false);
    const [isContactSearchOpen, setContactSearchOpen] = useState(false);
    const [editingContact, setEditingContact] = useState<PendingContact | null>(null);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [showAttachments, setShowAttachments] = useState(false);
    const [viewJobId, setViewJobId] = useState<string | null>(null);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const [smsModalState, setSmsModalState] = useState<{
        isOpen: boolean;
        recipientPhone: string;
        recipientName: string;
    }>({ isOpen: false, recipientPhone: '', recipientName: '' });
    const [smsSelectorState, setSmsSelectorState] = useState<{
        isOpen: boolean;
        recipientPhone: string;
        recipientName: string;
    }>({ isOpen: false, recipientPhone: '', recipientName: '' });

    // Share state
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [isCreatingShareToken, setIsCreatingShareToken] = useState(false);

    // Signature state
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
    const [showSignatureOverrideConfirm, setShowSignatureOverrideConfirm] = useState(false);
    const [contactIdPendingRemove, setContactIdPendingRemove] = useState<string | null>(null);
    const [showItemEditSignatureWarning, setShowItemEditSignatureWarning] = useState(false);
    const [pendingItemOperations, setPendingItemOperations] = useState<JobItemOperationDTO[] | null>(null);
    const pendingItemPromiseRef = useRef<{ resolve: () => void; reject: (e: Error) => void } | null>(null);
    const layer = useModalLayer({
        // viewJobId stays on this layer so Back closes Job Details, not the estimate.
        enabled: isOpen && !isContactFormOpen && !isContactSearchOpen && !shareModalOpen && !smsModalState.isOpen && !smsSelectorState.isOpen && !contactIdPendingRemove,
        onClose,
        historyState: { estimateDetailsModal: true },
    });

    // Simple counter for temporary IDs
    const nextContactIdRef = useRef(1);
    const initializedEstimateRef = useRef<string | null>(null);

    // Fetch estimate data
    const { data: estimate, isLoading, error } = useQuery({
        queryKey: ['estimate', estimateId],
        queryFn: () => apiService.getEstimateById(estimateId!),
        enabled: isOpen && !!estimateId,
    });

    const { data: attachmentsList } = useQuery({
        queryKey: ['estimateAttachments', estimateId],
        queryFn: () => apiService.getEstimateAttachments(estimateId!),
        enabled: isOpen && !!estimateId,
    });
    const attachmentCount = attachmentsList?.length ?? 0;

    // Opened from the list's convert icon: show the Convert dialog once, when the estimate has loaded.
    const initialActionAppliedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isOpen) {
            initialActionAppliedRef.current = null;
            return;
        }
        if (initialAction !== 'convert' || !estimate || initialActionAppliedRef.current === estimate.id) return;
        initialActionAppliedRef.current = estimate.id;
        if (estimate.status !== 'Converted') setShowConvertModal(true);
    }, [isOpen, initialAction, estimate]);

    // Fetch company invoice settings for tax rate
    const { data: companyInvoiceSettings } = useQuery<CompanyInvoiceDTO | null>({
        queryKey: ['company-invoice-settings', selectedCompanyId],
        queryFn: () => apiService.getCompanyInvoiceSettings(),
        enabled: isOpen && !!selectedCompanyId,
    });
    const taxRate = companyInvoiceSettings?.tax_rate ?? 0;

    // Amount shown in the share SMS/email; undefined at 0 so we never advertise "$0.00".
    const estimateTotal = calculateTotal(estimate?.items || [], taxRate);
    const shareAmountLabel = estimateTotal > 0 ? `$${estimateTotal.toFixed(2)}` : undefined;
    const companyTimeZone = companyTz?.trim();

    // Fetch job types for Convert to Job modal
    const { data: jobTypes } = useQuery({
        queryKey: ['jobTypes', selectedCompanyId],
        queryFn: () => apiService.getJobTypesByCompany(),
        enabled: isOpen && !!selectedCompanyId,
    });

    // Fetch technicians for assignment
    const { data: technicians } = useQuery({
        queryKey: ['technicians', selectedCompanyId],
        queryFn: () => apiService.getTechnicians(selectedCompanyId || undefined),
        enabled: isOpen && !!selectedCompanyId,
    });

    useEffect(() => {
        setShowInactiveTechnicians(false);
    }, [selectedCompanyId]);

    // Convert to Job mutation
    const convertToJobMutation = useMutation({
        mutationFn: (data: ConvertEstimateToJobDto) => apiService.convertEstimateToJob(estimateId!, data),
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['estimates'] });
            queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            setShowConvertModal(false);
            onEstimateUpdated?.();
            addNotification(notificationService.success('Estimate converted', 'The estimate was successfully converted to a job.'));
            onClose();
            openJob(result.job_id);
        },
        onError: (error: any) => {
            clientLogger.error('Failed to convert estimate:', error);
            addNotification(notificationService.error('Failed to convert', error.message ?? 'Please try again.'));
        },
    });

    // Signature handlers
    const handleSignEstimate = () => {
        if (!estimate) return;
        if (estimate.signature_path && !estimate.is_signature_voided) {
            setShowSignatureOverrideConfirm(true);
        } else {
            setShowSignatureModal(true);
        }
    };

    const handleSignatureSubmit = async (signaturePath: string, signedByName: string) => {
        if (!estimateId || !estimate) return;
        setIsSubmittingSignature(true);
        try {
            await apiService.submitEstimateSignature(estimateId, signaturePath, signedByName);

            queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
            queryClient.invalidateQueries({ queryKey: ['estimates'] });

            queryClient.setQueryData(['estimate', estimateId], {
                ...estimate,
                signature_path: signaturePath,
                signed_by_name: signedByName,
                is_signature_voided: false,
                viewed_at: new Date().toISOString(),
                signed_at: new Date().toISOString()
            });

            onEstimateUpdated?.();
            setShowSignatureModal(false);
            addNotification(notificationService.success('Signature saved', 'The signature has been saved to the estimate.'));
        } catch (error: any) {
            clientLogger.error('Failed to submit signature:', error);
            addNotification(notificationService.error('Failed to save signature', error.message ?? 'Please try again.'));
        } finally {
            setIsSubmittingSignature(false);
        }
    };

    const handleConfirmSignatureOverride = () => {
        setShowSignatureOverrideConfirm(false);
        setShowSignatureModal(true);
    };

    const handleCancelSignatureOverride = () => {
        setShowSignatureOverrideConfirm(false);
    };

    // PDF download handler
    const handleDownloadPdf = async () => {
        if (!estimateId) {
            addNotification(notificationService.error('Cannot download PDF', 'Missing estimate ID.'));
            return;
        }
        setIsDownloadingPdf(true);
        try {
            const { url } = await apiService.getEstimatePdfUrl(estimateId);
            await openDownloadUrl(url);
            addNotification(notificationService.success('PDF ready', 'The estimate PDF has been opened in a new tab.'));
        } catch (error: any) {
            clientLogger.error('Failed to download PDF:', error);
            addNotification(notificationService.error('Failed to download PDF', error.message ?? 'Please try again.'));
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    // Share handler
    const handleShare = async () => {
        if (!estimateId) return;
        setIsCreatingShareToken(true);
        try {
            const result = await apiService.createEstimateToken(estimateId);
            const publicBaseUrl = getAppOrigin();
            const url = result.short_code
              ? `${publicBaseUrl}/s/${result.short_code}`
              : `${publicBaseUrl}/public/estimates/${estimateId}/${result.token}`;
            setShareUrl(url);
            setShareModalOpen(true);
            // Refresh estimate to reflect any status change (e.g. Draft -> Sent)
            queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
            onEstimateUpdated?.();
        } catch (error: any) {
            clientLogger.error('Failed to create share token:', error);
            addNotification(notificationService.error('Failed to share', error.message ?? 'Please try again.'));
        } finally {
            setIsCreatingShareToken(false);
        }
    };

    // Initialize form when estimate loads (once per estimateId; refetches after saves won't reset)
    useEffect(() => {
        if (estimate && estimateId !== initializedEstimateRef.current) {
            initializedEstimateRef.current = estimateId;
            setDate(estimate.date?.split('T')[0] || '');
            setExpirationDate(estimate.expiration_date?.split('T')[0] || '');
            setNotes(estimate.notes || '');
            setAddress(estimate.address || '');
            setUnitName(estimate.unit_name || '');
            setJobTypeId(estimate.job_type_id || '');
            setAssignedTechnicianId(estimate.assigned_technician?.id || '');
            setStatus(estimate.status);
            setContacts(estimate.contacts?.map((c, index) => ({
                id: c.contact.id,
                contact: c.contact,
                notes: c.notes,
                isPrimary: index === 0,
            })) || []);
        }
    }, [estimate, estimateId]);

    // Re-initialize on next open after modal is closed
    useEffect(() => {
        if (!isOpen) initializedEstimateRef.current = null;
    }, [isOpen]);

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: (data: UpdateEstimateDto) => apiService.updateEstimate(estimateId!, data),
        onSuccess: (updatedEstimate, variables) => {
            // Update cache directly so the initialization guard prevents a state reset
            queryClient.setQueryData(['estimate', estimateId], updatedEstimate);
            queryClient.invalidateQueries({ queryKey: ['estimates'] });
            // Sync contacts from server response after contact operations
            if (variables.contact_operations?.length && updatedEstimate?.contacts) {
                setContacts(updatedEstimate.contacts.map((c: any, index: number) => ({
                    id: c.contact.id,
                    contact: c.contact,
                    notes: c.notes,
                    isPrimary: index === 0,
                })));
            }
            onEstimateUpdated?.();
        },
        onError: (error: any) => {
            clientLogger.error('Failed to update estimate:', error);
            addNotification(notificationService.error('Failed to update estimate', error.message ?? 'Please try again.'));
        },
    });

    // Immediate save helper — only sends the changed fields (API supports partial updates)
    const saveImmediate = (fields: Partial<UpdateEstimateDto>) => {
        updateMutation.mutate(fields as UpdateEstimateDto);
    };

    const handleEstimateItemsChange = async (operations: JobItemOperationDTO[]) => {
        if (!estimate) return;

        if (estimate.signature_path && !estimate.is_signature_voided) {
             const wouldChangeAmount = operations.some(op => {
                   if (op.operation === 'Add' || op.operation === 'Remove') return true;
                   if (op.operation === 'Edit' && op.item.id) {
                       const existing = (estimate.items || []).find((i: any) => (i as any).id === op.item.id);
                       if (!existing) return true;
                       return (
                           existing.price !== op.item.price ||
                           existing.quantity !== op.item.quantity ||
                           existing.discount !== op.item.discount ||
                           existing.taxable !== op.item.taxable
                       );
                   }
                   return false;
             });

             if (wouldChangeAmount) {
                 return new Promise<void>((resolve, reject) => {
                     setPendingItemOperations(operations);
                     pendingItemPromiseRef.current = { resolve, reject };
                     setShowItemEditSignatureWarning(true);
                 });
             }
        }
        await executeItemOperations(operations);
    };

    const executeItemOperations = async (operations: JobItemOperationDTO[]) => {
        if (!estimateId) return;
        
        try {
            const formattedOps = operations.map(op => ({
                ...op,
                item: {
                    ...op.item,
                    description: op.item.description || ''
                }
            })) as any[];
            
            await updateMutation.mutateAsync({ item_operations: formattedOps });
        } catch (error) {
            clientLogger.error('Failed to update estimate items:', error);
            throw error;
        }
    };

    const handleConfirmItemEditSignatureWarning = async () => {
        setShowItemEditSignatureWarning(false);
        if (pendingItemOperations) {
            try {
                await executeItemOperations(pendingItemOperations);
                pendingItemPromiseRef.current?.resolve();
            } catch (e) {
                pendingItemPromiseRef.current?.reject(e as Error);
            } finally {
                setPendingItemOperations(null);
                pendingItemPromiseRef.current = null;
            }
        }
    };

    const handleCancelItemEditSignatureWarning = () => {
        setShowItemEditSignatureWarning(false);
        setPendingItemOperations(null);
        pendingItemPromiseRef.current?.reject(new Error('cancelled'));
        pendingItemPromiseRef.current = null;
    };

    // Contact management
    const handleContactFormSubmit = async ({ contact }: {
        contact: ContactCreateDTO | ContactUpdateDTO;
        assignmentNotes?: string;
    }) => {
        const tempId = `contact-${nextContactIdRef.current++}`;
        const createPayload: ContactCreateDTO = {
            name: contact.name ?? '',
            phone: contact.phone ?? '',
            additional_phone: (contact as ContactUpdateDTO).additional_phone,
            email: contact.email,
            notes: contact.notes,
        };
        const entry: PendingContact = {
            id: tempId,
            contact: { id: tempId, name: contact.name ?? '', phone: contact.phone ?? '', additional_phone: (contact as ContactUpdateDTO).additional_phone, email: contact.email, notes: contact.notes },
            createPayload,
            isPrimary: contacts.filter(c => !c.isDeleted).length === 0,
            isNew: true,
        };
        setContacts(prev => [...prev, entry]);
        setContactFormOpen(false);
        saveImmediate({ contact_operations: [{ operation: 'Add', contact: { contact: createPayload } }] });
    };

    const handleSelectExistingContact = (contact: ContactDTO) => {
        setContactSearchOpen(false);
        if (contacts.some(item => item.id === contact.id && !item.isDeleted)) return;
        const entry: PendingContact = {
            id: contact.id,
            contact,
            notes: undefined,
            isPrimary: contacts.filter(c => !c.isDeleted).length === 0,
            isNew: true,
        };
        setContacts(prev => [...prev, entry]);
        saveImmediate({ contact_operations: [{ operation: 'Add', contact: { id: contact.id } }] });
    };

    const removeContact = (id: string) => {
        const target = contacts.find(c => c.id === id);
        setContacts(prev => prev.map(c => {
            if (c.id === id) {
                if (c.isNew) return null as any;
                return { ...c, isDeleted: true };
            }
            return c;
        }).filter(Boolean));
        if (target && !target.isNew) {
            saveImmediate({ contact_operations: [{ operation: 'Remove', contact: { id } }] });
        }
    };

    // Contact editing
    const handleEditContact = (pendingContact: PendingContact) => {
        setEditingContact(pendingContact);
        setContactFormOpen(true);
    };

    const handleEditContactSubmit = async ({ contact }: {
        contact: ContactCreateDTO | ContactUpdateDTO;
        assignmentNotes?: string;
    }) => {
        if (!editingContact) return;
        // Update local state immediately
        setContacts(prev => prev.map(c => {
            if (c.id === editingContact.id) {
                return {
                    ...c,
                    contact: {
                        ...c.contact,
                        name: contact.name ?? c.contact.name,
                        phone: contact.phone ?? c.contact.phone,
                        additional_phone: (contact as ContactUpdateDTO).additional_phone,
                        email: contact.email,
                    },
                };
            }
            return c;
        }));
        setContactFormOpen(false);
        setEditingContact(null);
        // Send edit operation to backend
        saveImmediate({
            contact_operations: [{
                operation: 'Edit',
                contact: {
                    id: editingContact.id,
                    contact: {
                        name: contact.name ?? '',
                        phone: contact.phone ?? '',
                        additional_phone: (contact as ContactUpdateDTO).additional_phone,
                        email: contact.email,
                    },
                },
            }],
        });
    };

    const setAsPrimary = (id: string) => {
        setContacts(prev => prev.map(c => ({ ...c, isPrimary: c.id === id })));
    };

    if (!isOpen) return null;

    const visibleContacts = contacts.filter(c => !c.isDeleted);
    const isReadOnly = status === 'Converted';

    // ==================== SHARED FIELD HELPERS ====================
    const renderDatesFields = () => (
        <>
            <div>
                <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">Estimate Date</label>
                <EditableField
                    value={date}
                    type="date"
                    disabled={isReadOnly}
                    inline={true}
                    onSave={async (val) => { setDate(val); saveImmediate({ date: val || undefined }); }}
                    renderValue={(val) => (
                        <span className="text-gray-900 font-medium text-sm">
                            {val ? formatDateOnlyInCompanyTimeZone(val, companyTz) : <span className="text-gray-400">Not set</span>}
                        </span>
                    )}
                />
            </div>
            <div>
                <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide block mb-1">Valid Until</label>
                <EditableField
                    value={expirationDate}
                    type="date"
                    disabled={isReadOnly}
                    inline={true}
                    onSave={async (val) => { setExpirationDate(val); saveImmediate({ expiration_date: val || undefined }); }}
                    renderValue={(val) => (
                        <span className="text-gray-900 font-medium text-sm">
                            {val ? formatDateOnlyInCompanyTimeZone(val, companyTz) : <span className="text-gray-400">Not set</span>}
                        </span>
                    )}
                />
            </div>
        </>
    );

    const renderAddressField = () => (
        <div>
            <label className="text-xs font-semibold text-emerald-600 uppercase tracking-wide flex items-center gap-1 mb-1">
                <MapPin className="h-3 w-3" /> Address
            </label>
            <EditableAddress
                value={address}
                showUnitField={true}
                unitValue={unitName}
                placeholder="Add address..."
                disabled={isReadOnly}
                onSave={async (newAddress, newUnit) => {
                    setAddress(newAddress);
                    if (newUnit !== undefined) setUnitName(newUnit);
                    saveImmediate({ address: newAddress || undefined, unit_name: newUnit || undefined });
                }}
            />
        </div>
    );

    const renderJobDetailsFields = () => (
        <>
            <div>
                <label className="text-xs font-semibold text-violet-600 uppercase tracking-wide block mb-1">Job Type</label>
                <EditableField
                    value={jobTypeId}
                    type="select"
                    options={[
                        { value: '', label: 'None' },
                        ...(jobTypes?.map((jt: any) => ({ value: jt.id, label: jt.name })) || [])
                    ]}
                    disabled={isReadOnly}
                    inline={true}
                    onSave={async (val) => { setJobTypeId(val); saveImmediate({ job_type_id: val || undefined }); }}
                    renderValue={(val) => {
                        const jt = jobTypes?.find((j: any) => j.id === val);
                        return <span className="text-gray-900 text-sm">{jt ? jt.name : <span className="text-gray-400 italic">None</span>}</span>;
                    }}
                />
            </div>
            <div>
                <label className="text-xs font-semibold text-violet-600 uppercase tracking-wide block mb-1">Assigned Technician</label>
                <EditableField
                    value={assignedTechnicianId}
                    type="select"
                    options={buildTechnicianSelectOptions(
                        technicians,
                        showInactiveTechnicians,
                        assignedTechnicianId,
                        { value: '', label: 'Unassigned' },
                    )}
                    disabled={isReadOnly}
                    inline={true}
                    onSave={async (val) => { setAssignedTechnicianId(val); saveImmediate({ assigned_technician_id: val || undefined }); }}
                    selectFooterAction={hasInactiveTechnicians(technicians) && !showInactiveTechnicians
                        ? {
                            label: 'Show inactive technicians...',
                            onClick: () => setShowInactiveTechnicians(true),
                        }
                        : undefined}
                    renderValue={(val) => {
                        const emp = technicians?.find((e: Technician) => e.id === val);
                        return <span className="text-gray-900 text-sm">{emp ? formatTechnicianOptionLabel(emp.name, emp.status) : <span className="text-gray-400 italic">Unassigned</span>}</span>;
                    }}
                />
            </div>
            {estimate?.created_by && (
                <div className="flex items-center gap-2 text-sm text-gray-600 pt-1">
                    <User className="h-3.5 w-3.5" />
                    <span>Created by {estimate.created_by.name}</span>
                </div>
            )}
        </>
    );

    const renderNotesField = () => (
        <div>
            <label className="text-xs font-semibold text-amber-600 uppercase tracking-wide block mb-1">Notes</label>
            <EditableField
                value={notes}
                type="textarea"
                placeholder="Add notes about this estimate..."
                disabled={isReadOnly}
                inline={true}
                onSave={async (val) => { setNotes(val); saveImmediate({ notes: val || undefined }); }}
                renderValue={(val) => (
                    <span className="text-gray-900 text-sm whitespace-pre-wrap">{val || <span className="text-gray-400 italic">No notes</span>}</span>
                )}
            />
        </div>
    );

    // Shared contacts rendering; sits in the client card together with the address, like the invoice's client box
    const renderContactsSection = () => (
        <div>
            <div className="flex items-center justify-between gap-2 mb-1">
                <label className="text-xs font-semibold text-emerald-600 uppercase tracking-wide flex items-center gap-1">
                    <User className="h-3 w-3" /> Contacts
                </label>
                {!isReadOnly && (
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => { setEditingContact(null); setContactFormOpen(true); }}
                            className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                        >
                            <Plus className="h-3 w-3" />
                            New
                        </button>
                        <button
                            type="button"
                            onClick={() => setContactSearchOpen(true)}
                            className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800"
                        >
                            <Search className="h-3 w-3" />
                            Existing
                        </button>
                    </div>
                )}
            </div>
            {visibleContacts.length === 0 ? (
                <div className="text-sm text-gray-400 italic">No contacts</div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {visibleContacts.map(c => (
                        <div key={c.id} className="flex items-center gap-2 px-3 py-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-sm font-medium text-gray-900 truncate">{c.contact.name || 'No name'}</span>
                                    {c.isPrimary && (
                                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full shrink-0">Primary</span>
                                    )}
                                </div>
                                {(c.contact.phone || c.contact.additional_phone || c.contact.email) && (
                                    <div className="text-xs text-gray-500 truncate">
                                        {[
                                            c.contact.phone && formatPhoneNumber(c.contact.phone),
                                            c.contact.additional_phone && formatPhoneNumber(c.contact.additional_phone),
                                            c.contact.email,
                                        ].filter(Boolean).join(' · ')}
                                    </div>
                                )}
                            </div>
                            {!isReadOnly && (
                                <div className="flex items-center shrink-0">
                                    {!c.isPrimary && (
                                        <button type="button" onClick={() => setAsPrimary(c.id)} className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded" title="Make primary">
                                            <Star className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                    <button type="button" onClick={() => handleEditContact(c)} className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit contact">
                                        <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button type="button" onClick={() => setContactIdPendingRemove(c.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Remove contact">
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Shared signature section
    const renderSignatureSection = () => (
        <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                    <span className="text-gray-600">Viewed</span>
                    <span className={`font-medium ${estimate?.viewed_at ? 'text-green-600' : 'text-gray-400'}`}>
                        {estimate?.viewed_at
                            ? formatInstantInCompanyTimeZone(estimate.viewed_at, companyTimeZone)
                            : 'Not viewed'}
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-gray-600">Signed</span>
                    <span className={`font-medium ${(estimate?.signature_path && !estimate?.is_signature_voided) ? 'text-green-600' : 'text-gray-400'}`}>
                        {(estimate?.signature_path && !estimate?.is_signature_voided) ? 'Yes' : 'Not signed'}
                    </span>
                </div>
                {estimate?.signed_at && !estimate?.is_signature_voided && (
                    <div className="flex justify-between items-center">
                        <span className="text-gray-600">Signed at</span>
                        <span className="font-medium text-gray-900">
                            {formatInstantInCompanyTimeZone(
                                estimate.signed_at.endsWith('Z') ? estimate.signed_at : `${estimate.signed_at}Z`,
                                companyTimeZone,
                            )}
                        </span>
                    </div>
                )}
                {estimate?.signature_path && !estimate?.is_signature_voided && (
                    <div className="border border-gray-200 rounded-md p-2 bg-gray-50 mt-2">
                        {estimate.signature_path.startsWith('data:') ||
                            estimate.signature_path.startsWith('http') ||
                            estimate.signature_path.startsWith('blob:') ? (
                            <img
                                src={estimate.signature_path}
                                alt="Client signature"
                                className="max-w-full h-20 object-contain mx-auto"
                            />
                        ) : (
                            <svg
                                viewBox="0 0 400 200"
                                className="w-full h-20"
                                role="img"
                                aria-label="Client signature"
                            >
                                <path
                                    d={estimate.signature_path}
                                    fill="none"
                                    stroke="black"
                                    strokeWidth="2"
                                />
                            </svg>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // Shared modals
    function renderModals() {
        return (
            <>
                {/* Contact Form Modal */}
                <ContactFormModal
                    isOpen={isContactFormOpen}
                    onClose={() => { setContactFormOpen(false); setEditingContact(null); }}
                    onSubmit={editingContact ? handleEditContactSubmit : handleContactFormSubmit}
                    contact={editingContact?.contact}
                    title={editingContact ? 'Edit Contact' : 'New Contact'}
                    confirmLabel={editingContact ? 'Save Changes' : 'Create Contact'}
                    isSubmitting={false}
                />

                {/* Contact Search Modal */}
                <ContactSearchModal
                    isOpen={isContactSearchOpen}
                    onClose={() => setContactSearchOpen(false)}
                    onSelect={handleSelectExistingContact}
                />

                {/* Convert to Job Modal */}
                {showConvertModal && (
                    <LayeredDialog onClose={() => setShowConvertModal(false)} historyState={{ estimateConvertModal: true }}>
                        {(handleConvertClose) => (
                        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold">Convert to Job</h3>
                                <button onClick={handleConvertClose} className="p-1 hover:bg-gray-100 rounded">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mb-4">
                                This will create a new job from this estimate. The estimate status will be marked as &quot;Converted&quot;.
                            </p>
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                convertToJobMutation.mutate({
                                    scheduled_date: scheduleDate ? new Date(`${scheduleDate}:00Z`).toISOString() : undefined
                                });
                            }}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Date</label>
                                    <input
                                        type="datetime-local"
                                        value={scheduleDate}
                                        onChange={(e) => setScheduleDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={handleConvertClose}
                                        className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={convertToJobMutation.isPending}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {convertToJobMutation.isPending ? (
                                            <><LoadingSpinner /> Converting...</>
                                        ) : (
                                            <><ArrowRightCircle className="h-4 w-4" /> Convert</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                        )}
                    </LayeredDialog>
                )}

                {/* SMS Modal (Desktop) */}
                {!isMobile && (
                    <SendSmsModal
                        isOpen={smsModalState.isOpen}
                        onClose={() => setSmsModalState({ ...smsModalState, isOpen: false })}
                        recipientPhone={smsModalState.recipientPhone}
                        recipientName={smsModalState.recipientName}
                        recipientType="Client"
                        estimateId={estimateId ?? undefined}
                    />
                )}

                {/* SMS Method Selector (Mobile) */}
                {isMobile && (
                    <SmsMethodSelector
                        isOpen={smsSelectorState.isOpen}
                        onClose={() => setSmsSelectorState({ ...smsSelectorState, isOpen: false })}
                        recipientPhone={smsSelectorState.recipientPhone}
                        recipientName={smsSelectorState.recipientName}
                        recipientType="Client"
                        estimateId={estimateId ?? undefined}
                    />
                )}

                {estimateId && (
                    <EstimateAttachmentsModal
                        isOpen={showAttachments}
                        onClose={() => setShowAttachments(false)}
                        estimateId={estimateId}
                        readOnly={isReadOnly}
                        onAttachmentsUpdated={() => queryClient.invalidateQueries({ queryKey: ['estimateAttachments', estimateId] })}
                    />
                )}

                {/* Converted job — stacks above this modal via the inherited LayerDepthContext */}
                {viewJobId && (
                    <JobDetailsModal jobId={viewJobId} isOpen onClose={() => setViewJobId(null)} />
                )}

                {/* Share Modal */}
                <ShareModal
                    isOpen={shareModalOpen}
                    onClose={() => setShareModalOpen(false)}
                    shareUrl={shareUrl}
                    recipientPhone={contacts[0]?.contact?.phone || ''}
                    recipientName={contacts[0]?.contact?.name || ''}
                    recipientEmail={(contacts[0]?.contact as any)?.email || ''}
                    entityType="estimate"
                    entityNumber={estimate?.estimate_number || ''}
                    amountLabel={shareAmountLabel}
                    estimateId={estimateId ?? undefined}
                />

                {/* Signature Pad Modal */}
                {showSignatureModal && (
                    <LayeredDialog onClose={() => setShowSignatureModal(false)} historyState={{ estimateSignatureModal: true }} closeOnBackdrop>
                        {(handleSignatureClose) => (
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative z-10 animate-in zoom-in-95">
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Sign Estimate</h3>
                                <p className="text-sm text-gray-500">Please provide your signature below.</p>
                            </div>
                            <SignaturePad
                                onSignatureComplete={handleSignatureSubmit}
                                onCancel={handleSignatureClose}
                                initialSignaturePath={(estimate?.signature_path && !estimate?.is_signature_voided) ? estimate.signature_path : undefined}
                                initialSignerName={(estimate?.signature_path && !estimate?.is_signature_voided) ? estimate.signed_by_name : undefined}
                            />
                            {isSubmittingSignature && (
                                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-20 rounded-xl">
                                    <LoadingSpinner />
                                    <span className="ml-2">Saving...</span>
                                </div>
                            )}
                        </div>
                        )}
                    </LayeredDialog>
                )}

                {/* Signature Override Confirm Modal */}
                <ConfirmDialog
                    isOpen={showSignatureOverrideConfirm}
                    onClose={handleCancelSignatureOverride}
                    onConfirm={handleConfirmSignatureOverride}
                    title="Replace Signature?"
                    message="This estimate already has a valid signature. Are you sure you want to replace it? The existing signature will be permanently voided."
                    confirmText="Replace"
                    confirmButtonClassName="bg-blue-600 hover:bg-blue-700 text-white"
                />
                {/* Item Edit Signature Warning Modal */}
                <ConfirmDialog
                    isOpen={showItemEditSignatureWarning}
                    onClose={handleCancelItemEditSignatureWarning}
                    onConfirm={handleConfirmItemEditSignatureWarning}
                    title="Editing May Void Signature"
                    message="This estimate has been signed by the customer. Editing items may change the total, which will void their signature."
                    confirmText="Continue"
                />

                <ConfirmDialog
                    isOpen={!!contactIdPendingRemove}
                    onClose={() => setContactIdPendingRemove(null)}
                    onConfirm={() => {
                        if (contactIdPendingRemove) {
                            removeContact(contactIdPendingRemove);
                        }
                    }}
                    title="Remove contact?"
                    message={
                        <div className="space-y-2">
                            <span>{destructiveCopy.removeEstimateContact}</span>
                            <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
                        </div>
                    }
                    confirmText="Remove"
                />
            </>
        );
    }

    // ==================== LOADING / ERROR STATE ====================
    if (isLoading || error) {
        return createPortal(
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" style={layer.style}>
                <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100">
                                <FileText className="h-5 w-5 text-blue-600" />
                            </div>
                            <span className="font-semibold text-gray-900">Estimate</span>
                        </div>
                        <button onClick={layer.onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    {error ? (
                        <div className="flex flex-col items-center justify-center p-8 text-center">
                            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                                <AlertCircle className="h-8 w-8 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Estimate</h3>
                            <p className="text-sm text-gray-600 mb-4 max-w-sm">An error occurred while loading the estimate.</p>
                            <button
                                onClick={layer.onClose}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4 p-6">
                            <div className="flex flex-col items-center justify-center py-8">
                                <LoadingSpinner />
                                <p className="mt-4 text-sm text-gray-600">Loading estimate...</p>
                            </div>
                            <div className="animate-pulse space-y-4">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-12 h-12 rounded-full bg-gray-200" />
                                    <div className="space-y-2">
                                        <div className="h-5 w-40 bg-gray-200 rounded" />
                                        <div className="h-4 w-24 bg-gray-200 rounded" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <div className="space-y-3">
                                        <div className="h-32 bg-gray-200 rounded-lg" />
                                        <div className="h-24 bg-gray-200 rounded-lg" />
                                    </div>
                                    <div className="lg:col-span-2 h-64 bg-gray-200 rounded-lg" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>,
            document.body
        );
    }

    // Shown in both headers when the estimate was created from a job that still exists.
    const sourceJobLink = estimate?.source_job_id && (
        <button
            type="button"
            onClick={() => setViewJobId(estimate.source_job_id!)}
            title="Open the job"
            className="group mt-1 inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 pl-2.5 pr-1 py-1 text-xs font-semibold text-blue-700 shadow-sm hover:bg-blue-100 hover:border-blue-400 transition-colors"
        >
            <Briefcase className="h-3.5 w-3.5" />
            {estimate.source_job_number ? `Created from job #${estimate.source_job_number}` : 'Created from job'}
            <span className="ml-1 inline-flex items-center gap-0.5 rounded-md bg-blue-600 px-1.5 py-0.5 text-white group-hover:bg-blue-700">
                Open
                <ArrowUpRight className="h-3 w-3" />
            </span>
        </button>
    );

    // ==================== MOBILE VIEW ====================
    if (isMobile) {
        return createPortal(
            <LayerDepthContext.Provider value={layer.depth}>
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-0" style={layer.style}>
                <div className="bg-gray-50 w-full h-full overflow-hidden flex flex-col relative safe-area-top">

                    {/* Header - Compact */}
                    <div className="bg-white border-b border-gray-200 z-10 shrink-0 touch-none">
                        <div className="px-4 py-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 shrink-0">
                                    <FileText className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="min-w-0">
                                    <h2 className="text-lg font-bold text-gray-900 leading-tight truncate">
                                        Estimate #{estimate?.estimate_number || '...'}
                                    </h2>
                                    <p className="text-xs text-gray-500 truncate">
                                        {date ? formatDateOnlyInCompanyTimeZone(date, companyTz) : ''}
                                        {expirationDate && ` • Valid until ${formatDateOnlyInCompanyTimeZone(expirationDate, companyTz)}`}
                                    </p>
                                    {sourceJobLink}
                                </div>
                            </div>
                            <EditableField
                                value={status}
                                type="select"
                                options={[
                                    { value: 'Draft', label: 'Draft' },
                                    { value: 'Sent', label: 'Sent' },
                                    { value: 'Expired', label: 'Expired' },
                                ]}
                                disabled={isReadOnly}
                                inline={true}
                                onSave={async (val) => { setStatus(val as EstimateStatus); saveImmediate({ status: val as EstimateStatus }); }}
                                renderValue={(val) => (
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${statusColors[val as EstimateStatus]?.bg || 'bg-gray-100'} ${statusColors[val as EstimateStatus]?.text || 'text-gray-600'}`}>
                                        {val}
                                    </span>
                                )}
                            />
                            <button onClick={layer.onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full shrink-0">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Main Content - Scrollable */}
                    <div className={`flex-1 overflow-y-auto overscroll-y-none ${isKeyboardOpen ? '' : 'pb-footer-safe'}`}>
                        <div className="p-4 space-y-4">

                            {/* Card 2: Dates */}
                            <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="space-y-3">
                                    {renderDatesFields()}
                                </div>
                            </div>

                            {/* Card 3: Contacts & Address */}
                            <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="space-y-2">
                                    {renderContactsSection()}
                                    {renderAddressField()}
                                </div>
                            </div>

                            {/* Card 5: Job Details */}
                            <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                <div className="space-y-3">
                                    {renderJobDetailsFields()}
                                </div>
                            </div>

                            {/* Card 6: Notes */}
                            <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                {renderNotesField()}
                            </div>

                            {/* Card 7: Items - Collapsible, collapsed by default */}
                            <JobItemsSection
                                items={estimate?.items || []}
                                companyId={selectedCompanyId || undefined}
                                taxRate={taxRate}
                                defaultItemTaxable={companyInvoiceSettings?.default_item_taxable ?? true}
                                onItemsChange={handleEstimateItemsChange}
                                isEditable={!isReadOnly}
                                collapsible
                                defaultCollapsed
                            />

                            {/* Payments (deposits) */}
                            {estimate && <EstimatePaymentsSection estimate={estimate} onViewJob={setViewJobId} />}

                            {/* Card 8: Signature & Status */}
                            <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                {renderSignatureSection()}
                            </div>

                        </div>
                    </div>

                    {/* Sticky Footer Action Bar */}
                    {!isKeyboardOpen && (
                    <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom-padded z-20 touch-none">
                        <div className="grid grid-cols-6 gap-1 px-2 pt-2">
                            <button
                                onClick={handleSignEstimate}
                                disabled={isSubmittingSignature || isReadOnly}
                                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                    {isSubmittingSignature ? (
                                        <LoadingSpinner />
                                    ) : (
                                        <Edit3 className="h-5 w-5 text-gray-700" />
                                    )}
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">Sign</span>
                            </button>

                            <button
                                onClick={handleDownloadPdf}
                                disabled={isDownloadingPdf}
                                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                    {isDownloadingPdf ? (
                                        <LoadingSpinner />
                                    ) : (
                                        <Download className="h-5 w-5 text-gray-700" />
                                    )}
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">{isDownloadingPdf ? 'Generating...' : 'PDF'}</span>
                            </button>

                            <button
                                onClick={handleShare}
                                disabled={isCreatingShareToken}
                                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                    <Share2 className="h-5 w-5 text-gray-700" />
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">{isCreatingShareToken ? 'Wait...' : 'Share'}</span>
                            </button>

                            <button
                                onClick={() => setShowAttachments(true)}
                                className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors"
                                title={attachmentCount > 0 ? `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}` : undefined}
                            >
                                <div className="relative flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white shadow-sm">
                                    <Paperclip className="h-5 w-5 text-gray-700" />
                                    {attachmentCount > 0 && (
                                        <span
                                            className="absolute -top-0.5 -right-0.5 min-h-[1.125rem] min-w-[1.125rem] px-1 flex items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white leading-none shadow-sm ring-2 ring-white"
                                            aria-hidden
                                        >
                                            {attachmentCount > 99 ? '99+' : attachmentCount}
                                        </span>
                                    )}
                                </div>
                                <span className="text-[10px] font-medium text-gray-600">Attach</span>
                            </button>

                            {estimate && (
                                <EstimateTakePaymentButton
                                    estimate={estimate}
                                    renderCustom={(onClick, isDisabled) => (
                                        <button
                                            onClick={onClick}
                                            disabled={isDisabled}
                                            className={`flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors ${isDisabled ? 'opacity-50' : ''}`}
                                        >
                                            <div className={`flex items-center justify-center w-10 h-10 rounded-full border shadow-sm ${isDisabled ? 'border-gray-200 bg-gray-50' : 'border-green-300 bg-green-50'}`}>
                                                <DollarSign className={`h-5 w-5 ${isDisabled ? 'text-gray-400' : 'text-green-600'}`} />
                                            </div>
                                            <span className="text-[10px] font-medium text-gray-600">Payment</span>
                                        </button>
                                    )}
                                />
                            )}

                            {status !== 'Converted' ? (
                                <button
                                    onClick={() => setShowConvertModal(true)}
                                    className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-blue-300 bg-blue-50 shadow-sm">
                                        <ArrowRightCircle className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <span className="text-[10px] font-medium text-gray-600">Convert</span>
                                </button>
                            ) : estimate?.converted_to_job_id ? (
                                <button
                                    onClick={() => setViewJobId(estimate.converted_to_job_id!)}
                                    className="flex flex-col items-center justify-center gap-1 p-1 rounded-lg hover:bg-gray-50 text-gray-600 active:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-blue-300 bg-blue-50 shadow-sm">
                                        <Briefcase className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <span className="text-[10px] font-medium text-gray-600">View Job</span>
                                </button>
                            ) : (
                                <div className="flex flex-col items-center justify-center gap-1 p-1 opacity-30">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white">
                                        <ArrowRightCircle className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <span className="text-[10px] font-medium text-gray-500">Converted</span>
                                </div>
                            )}
                        </div>
                    </div>
                    )}

                </div>

                {/* Modals */}
                {renderModals()}
            </div>
            </LayerDepthContext.Provider>,
            document.body
        );
    }

    // ==================== DESKTOP VIEW ====================
    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
        <div className="fixed inset-0 overflow-y-auto" style={layer.style}>
            <div className="flex items-center justify-center min-h-screen p-4">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm" onClick={layer.onClose} />

                {/* Modal panel */}
                <div className="relative bg-white rounded-xl text-left overflow-hidden shadow-xl w-full max-w-7xl max-h-[92vh] flex flex-col">
                    <div className="bg-white px-6 pt-5 pb-6 overflow-y-auto flex-1">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                                    <FileText className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-xl font-semibold text-secondary-900">
                                            Estimate #{estimate?.estimate_number || '...'}
                                        </h3>
                                        <EditableField
                                            value={status}
                                            type="select"
                                            options={[
                                                { value: 'Draft', label: 'Draft' },
                                                { value: 'Sent', label: 'Sent' },
                                                { value: 'Expired', label: 'Expired' },
                                            ]}
                                            disabled={isReadOnly}
                                            inline={true}
                                            onSave={async (val) => { setStatus(val as EstimateStatus); saveImmediate({ status: val as EstimateStatus }); }}
                                            renderValue={(val) => (
                                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusColors[val as EstimateStatus]?.bg || 'bg-gray-100'} ${statusColors[val as EstimateStatus]?.text || 'text-gray-600'}`}>
                                                    {val}
                                                </span>
                                            )}
                                        />
                                    </div>
                                    <p className="text-sm text-secondary-500">
                                        {date ? formatDateOnlyInCompanyTimeZone(date, companyTz) : ''}
                                        {expirationDate && ` • Valid until ${formatDateOnlyInCompanyTimeZone(expirationDate, companyTz)}`}
                                    </p>
                                    {sourceJobLink}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {estimate && <EstimateTakePaymentButton estimate={estimate} />}
                                <button
                                    onClick={handleDownloadPdf}
                                    disabled={isDownloadingPdf}
                                    className="btn-secondary flex items-center gap-2 px-3 py-2"
                                >
                                    {isDownloadingPdf ? <LoadingSpinner /> : <Download className="h-4 w-4" />}
                                    {isDownloadingPdf ? 'Generating...' : 'PDF'}
                                </button>
                                <button
                                    onClick={handleShare}
                                    disabled={isCreatingShareToken}
                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="h-4 w-4" />
                                    {isCreatingShareToken ? 'Creating...' : 'Send'}
                                </button>
                                <button
                                    onClick={() => setShowAttachments(true)}
                                    className="btn-secondary flex items-center gap-2 px-3 py-2"
                                    title={attachmentCount > 0 ? `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}` : undefined}
                                >
                                    <span className="relative inline-flex shrink-0">
                                        <Paperclip className="h-4 w-4" />
                                        {attachmentCount > 0 && (
                                            <span
                                                className="absolute -top-2 -right-2 min-h-[1.125rem] min-w-[1.125rem] px-1 flex items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white leading-none shadow-sm ring-2 ring-white"
                                                aria-hidden
                                            >
                                                {attachmentCount > 99 ? '99+' : attachmentCount}
                                            </span>
                                        )}
                                    </span>
                                    Estimate Attachments
                                </button>
                                {status !== 'Converted' ? (
                                    <button
                                        onClick={() => setShowConvertModal(true)}
                                        className="btn-primary flex items-center gap-2 px-3 py-2"
                                    >
                                        <ArrowRightCircle className="h-4 w-4" />
                                        Convert to Job
                                    </button>
                                ) : estimate?.converted_to_job_id && (
                                    <button
                                        onClick={() => setViewJobId(estimate.converted_to_job_id!)}
                                        className="btn-primary flex items-center gap-2 px-3 py-2"
                                    >
                                        <Briefcase className="h-4 w-4" />
                                        View Job
                                    </button>
                                )}
                                <button
                                    onClick={layer.onClose}
                                    className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                            {/* Left Column - Status, Dates, Contacts, Details */}
                            <div className="space-y-4">
                                {/* Dates */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="space-y-4">
                                        {renderDatesFields()}
                                    </div>
                                </div>

                                {/* Contacts & Address */}
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="space-y-3">
                                        {renderContactsSection()}
                                        {renderAddressField()}
                                    </div>
                                </div>

                                {/* Job Details */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="space-y-3">
                                        {renderJobDetailsFields()}
                                    </div>
                                </div>

                                {/* Notes */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    {renderNotesField()}
                                </div>

                                {/* Signature Status */}
                                {renderSignatureSection()}
                            </div>

                            {/* Right Column - Items, Payments */}
                            <div className="lg:col-span-3 space-y-4">
                                <JobItemsSection
                                    items={estimate?.items || []}
                                    companyId={selectedCompanyId || undefined}
                                    taxRate={taxRate}
                                    defaultItemTaxable={companyInvoiceSettings?.default_item_taxable ?? true}
                                    onItemsChange={handleEstimateItemsChange}
                                    isEditable={!isReadOnly}
                                />
                                {estimate && <EstimatePaymentsSection estimate={estimate} onViewJob={setViewJobId} />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {renderModals()}
        </div>
        </LayerDepthContext.Provider>,
        document.body
    );
}
